import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ValidateTransferRegistradorAccount, ValidateTwoValueInString } from 'src/app/helpers/Validators/specific-value-in-string-validator';
import { OperationTransferenceService } from 'src/app/services/operation/operation-transference.service';
import { CarbonOperation } from 'src/app/shared/interfaces/carbon-operation';
import { CarbonOperationType } from 'src/app/shared/interfaces/carbon-operation-type.enum';
import { CpfCnpjValidator } from 'src/app/helpers/Validators/CpfCnpjValidator/cpf-cnpj.validator';
import { comitenteBackendValidator } from 'src/app/helpers/Validators/ComitenteValidatorBackend/comitente-backend.validator';
import {
  extractDocumentCharacters,
  formatCNPJProgressive,
  formatCpfCnpjInputValue,
} from 'src/app/helpers/utils/cpf-cnpj.util';
import { CreditRegisterService } from '../../register/credit-register/service/credit-register.service';
import { InstrumentQuantityInfo } from 'src/app/shared/interfaces/instrument-quantity-info';
import { merge, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import {
  buildTransferenceResultModalViewModel,
  TransferenceModalAction,
  TransferenceResultModalViewModel,
} from './modals/transference-result-modal.model';

@Component({
  standalone: false,
  selector: 'app-operation-transference',
  templateUrl: './operation-transference.component.html',
  styleUrls: ['./operation-transference.component.scss'],
})
export class OperationTransferenceComponent implements OnInit, OnDestroy {

  formTransference: FormGroup = new FormGroup({});
  showcpfCnpjComitente = false;
  showPurchaserComitenteDocument = false;
  confirmation = false;
  comitenteFound = false;
  purchaserComitenteFound = false;
  instrumentQuantityInfo: InstrumentQuantityInfo | null = null;
  instrumentQuantityLoading = false;
  showTransferenceResultModal = false;
  transferenceResultModal: TransferenceResultModalViewModel | null = null;

  private subscriptions = new Subscription();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private operationService: OperationTransferenceService,
    private creditRegisterService: CreditRegisterService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initFormTransference();

    this.subscriptions.add(
      this.formTransference.get('holderCode')?.valueChanges.subscribe(value => {
        this.updateHolderComitenteField(value);
      }),
    );

    this.subscriptions.add(
      this.formTransference.get('purchaserCode')?.valueChanges.subscribe(value => {
        this.updatePurchaserComitenteField(value);
      }),
    );

    this.formTransference.get('cpfCnpjComitente')?.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateComitenteFoundState();
      });

    this.formTransference.get('cpfCnpjComitente')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.syncComitenteDocumentControlValue('cpfCnpjComitente', value);
        const digitCount = extractDocumentCharacters(value).length;
        if (digitCount === 11 || digitCount === 14 || this.comitenteFound) {
          this.updateComitenteFoundState();
        }
      });

    this.formTransference.get('purchaserComitenteDocument')?.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePurchaserComitenteFoundState();
      });

    this.formTransference.get('purchaserComitenteDocument')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.syncComitenteDocumentControlValue('purchaserComitenteDocument', value);
        const digitCount = extractDocumentCharacters(value).length;
        if (digitCount === 11 || digitCount === 14 || this.purchaserComitenteFound) {
          this.updatePurchaserComitenteFoundState();
        }
      });

    this.subscriptions.add(
      merge(
        this.formTransference.get('carbonCreditCode')!.valueChanges.pipe(distinctUntilChanged()),
        this.formTransference.get('holderCode')!.valueChanges.pipe(distinctUntilChanged()),
      ).pipe(
        debounceTime(400),
        switchMap(() => this.fetchInstrumentQuantity()),
      ).subscribe(info => {
        this.instrumentQuantityInfo = info;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.formTransference.get('creditQuantity')!.valueChanges.subscribe(() => {
        this.cdr.markForCheck();
      }),
    );

    this.refreshInstrumentQuantity();
  }

  onInstrumentFieldChange(): void {
    this.refreshInstrumentQuantity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  /** Máscara livre durante digitação; formatação CPF/CNPJ ocorre no blur e via sync. */
  get comitenteDocumentMask(): 'text' {
    return 'text';
  }

  get purchaserComitenteDocumentMask(): 'text' {
    return 'text';
  }

  public onChangesHolderComitenteCpfOrCnpj(): void {
    const control = this.formTransference.get('cpfCnpjComitente');
    const next = formatCpfCnpjInputValue(control?.value, true);
    if (control?.value !== next) {
      control?.setValue(next);
    }
  }

  public onChangesPurchaserComitenteCpfOrCnpj(): void {
    const control = this.formTransference.get('purchaserComitenteDocument');
    const next = formatCpfCnpjInputValue(control?.value, true);
    if (control?.value !== next) {
      control?.setValue(next);
    }
  }

  get showInstrumentQuantity(): boolean {
    return this.instrumentQuantityInfo?.available != null && this.instrumentQuantityInfo?.total != null;
  }

  get instrumentAvailableQuantity(): number | null {
    return this.instrumentQuantityInfo?.available ?? null;
  }

  get instrumentTotalQuantity(): number | null {
    return this.instrumentQuantityInfo?.total ?? null;
  }

  get remainingQuantity(): number | null {
    if (this.instrumentQuantityInfo?.available == null) {
      return null;
    }

    const rawQuantity = this.formTransference.get('creditQuantity')?.value;
    if (rawQuantity == null || rawQuantity === '') {
      return null;
    }

    const enteredQuantity = Number(rawQuantity);
    if (Number.isNaN(enteredQuantity)) {
      return null;
    }

    return Math.max(0, this.instrumentQuantityInfo.available - enteredQuantity);
  }

  get canSubmit(): boolean {
    return this.formTransference.valid && this.confirmation;
  }

  confirmationChange(event: Event): void {
    const customEvent = event as CustomEvent<{ selected?: boolean }>;
    this.confirmation = !!customEvent.detail?.selected;
  }

  private refreshInstrumentQuantity(): void {
    this.subscriptions.add(
      this.fetchInstrumentQuantity().subscribe(info => {
        this.instrumentQuantityInfo = info;
        this.cdr.markForCheck();
      }),
    );
  }

  private fetchInstrumentQuantity() {
    const carbonCreditCode = this.normalizeInstrumentCode(
      this.formTransference.get('carbonCreditCode')?.value,
    );
    const holderCode = (this.formTransference.get('holderCode')?.value || '').trim();

    if (carbonCreditCode.length !== 12 || holderCode.length !== 10) {
      this.instrumentQuantityInfo = null;
      this.instrumentQuantityLoading = false;
      return of(null);
    }

    this.instrumentQuantityLoading = true;
    return this.operationService.getInstrumentQuantity(carbonCreditCode, holderCode).pipe(
      catchError(() => of(null)),
      finalize(() => {
        this.instrumentQuantityLoading = false;
      }),
    );
  }

  private normalizeInstrumentCode(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  private updateHolderComitenteField(holderCode: string): void {
    const accountType = holderCode?.substring(6, 8);
    const cpfCnpjControl = this.formTransference.get('cpfCnpjComitente');
    this.showcpfCnpjComitente = accountType === '10';

    if (this.showcpfCnpjComitente) {
      cpfCnpjControl?.enable({ emitEvent: false });
      cpfCnpjControl?.setValidators([Validators.required, CpfCnpjValidator.validate]);
    } else {
      cpfCnpjControl?.clearValidators();
      cpfCnpjControl?.setValue('', { emitEvent: false });
      cpfCnpjControl?.disable({ emitEvent: false });
      this.comitenteFound = false;
    }
    cpfCnpjControl?.updateValueAndValidity();
  }

  private updatePurchaserComitenteField(purchaserCode: string): void {
    const accountType = purchaserCode?.substring(6, 8);
    const purchaserComitenteControl = this.formTransference.get('purchaserComitenteDocument');
    this.showPurchaserComitenteDocument = accountType === '10';

    if (this.showPurchaserComitenteDocument) {
      purchaserComitenteControl?.enable({ emitEvent: false });
      purchaserComitenteControl?.setValidators([Validators.required, CpfCnpjValidator.validate]);
    } else {
      purchaserComitenteControl?.clearValidators();
      purchaserComitenteControl?.setValue('', { emitEvent: false });
      purchaserComitenteControl?.disable({ emitEvent: false });
      this.purchaserComitenteFound = false;
    }
    purchaserComitenteControl?.updateValueAndValidity();
  }

  private updatePurchaserComitenteFoundState(): void {
    const control = this.formTransference.get('purchaserComitenteDocument');

    if (!control || !this.showPurchaserComitenteDocument) {
      this.purchaserComitenteFound = false;
      return;
    }

    const digits = String(control.value ?? '').replace(/\D/g, '');
    const hasFullDocument = digits.length === 11 || digits.length === 14;

    this.purchaserComitenteFound = hasFullDocument
      && control.status === 'VALID'
      && control.valid
      && !control.hasError('comitenteInvalido')
      && !control.hasError('backendError');
  }

  private updateComitenteFoundState(): void {
    const control = this.formTransference.get('cpfCnpjComitente');

    if (!control || !this.showcpfCnpjComitente) {
      this.comitenteFound = false;
      return;
    }

    const digits = String(control.value ?? '').replace(/\D/g, '');
    const hasFullDocument = digits.length === 11 || digits.length === 14;

    this.comitenteFound = hasFullDocument
      && control.status === 'VALID'
      && control.valid
      && !control.hasError('comitenteInvalido')
      && !control.hasError('backendError');
  }

  /** Durante a digitação: não formata CPF no 11º dígito (permite continuar para CNPJ). */
  private syncComitenteDocumentControlValue(
    controlName: 'cpfCnpjComitente' | 'purchaserComitenteDocument',
    raw: string | null | undefined,
  ): void {
    const control = this.formTransference.get(controlName);
    const alnum = extractDocumentCharacters(raw).slice(0, 14);

    if (!alnum) {
      if (control?.value) {
        control.setValue('', { emitEvent: false });
      }
      return;
    }

    let nextValue: string;
    if (alnum.length >= 12) {
      nextValue = formatCNPJProgressive(alnum);
    } else if (alnum.length === 11 && /[.\-/]/.test(String(raw ?? ''))) {
      nextValue = formatCpfCnpjInputValue(raw, true);
    } else {
      nextValue = alnum;
    }

    const currentAlnum = extractDocumentCharacters(control?.value);
    const isCompleteDocument = alnum.length === 11 || alnum.length === 14;

    if (currentAlnum === alnum && String(control?.value ?? '') === nextValue) {
      return;
    }

    control?.setValue(nextValue, { emitEvent: isCompleteDocument });
  }

  public initFormTransference(): void {
    this.formTransference = this.formBuilder.group({
      carbonCreditCode: ['', [Validators.required, Validators.maxLength(12), Validators.minLength(12)]],
      holderCode: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(10), ValidateTwoValueInString('00', '10', 6, 7)]],
      purchaserCode: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(10), ValidateTransferRegistradorAccount(6, 7)]],
      creditQuantity: ['', [Validators.required, Validators.min(0), Validators.pattern('^[1-9][0-9]*$')]],
      cpfCnpjComitente: ['', [], [
        comitenteBackendValidator(
          this.creditRegisterService,
          () => this.formTransference.get('holderCode')?.value,
        ),
      ]],
      purchaserComitenteDocument: ['', [], [
        comitenteBackendValidator(
          this.creditRegisterService,
          () => this.formTransference.get('purchaserCode')?.value,
        ),
      ]],
    });

    this.formTransference.get('cpfCnpjComitente')?.disable({ emitEvent: false });
    this.formTransference.get('purchaserComitenteDocument')?.disable({ emitEvent: false });
  }

  public transferenceRegister(): void {
    if (!this.canSubmit) {
      return;
    }

    const form = this.formTransference.getRawValue();
    const payload = { ...form, acceptanceIndicator: this.confirmation === true };
    this.operationService.transferenceRegister(payload, CarbonOperationType.TRANSFER_WITHOUT_FINANCIAL).subscribe({
      next: (value: CarbonOperation) => {
        this.openTransferenceResultModal(value);
        this.cleanForm();
      },
      error: (err: unknown) => {
        const message = (err as { error?: Array<{ message?: string }> })?.error?.[0]?.message;
        this.openTransferenceResultModal(typeof message === 'string' ? message : '');
      },
    });
  }

  openTransferenceResultModal(data: CarbonOperation | string): void {
    this.transferenceResultModal = buildTransferenceResultModalViewModel(data);
    this.showTransferenceResultModal = true;
  }

  onTransferenceResultModalAction(action: TransferenceModalAction): void {
    this.showTransferenceResultModal = false;

    if (action === 'consult') {
      this.navigateToOperationConsult();
    }

    if (action === 'clear') {
      this.cleanForm();
    }

    if (action === 'retry') {
      this.transferenceRegister();
    }
  }

  navigateToOperationConsult(): void {
    this.router.navigate(['/consulta-operacoes']);
  }

  public checkHasError(fieldName: string, errorCode?: string): boolean {
    const field = this.formTransference.get(fieldName);
    if (field == null || !field.touched) {
      return false;
    }
    if (errorCode == null) {
      return field.invalid;
    }
    return field.hasError(errorCode);
  }

  public cleanForm(): void {
    this.formTransference.reset();
    this.formTransference.get('cpfCnpjComitente')?.disable({ emitEvent: false });
    this.formTransference.get('purchaserComitenteDocument')?.disable({ emitEvent: false });
    this.confirmation = false;
    this.comitenteFound = false;
    this.purchaserComitenteFound = false;
    this.showcpfCnpjComitente = false;
    this.showPurchaserComitenteDocument = false;
    this.instrumentQuantityInfo = null;
    this.instrumentQuantityLoading = false;
  }
}
